// Infographic proposal template — the reusable section system.
//
// This file is the single definition of what the template can contain: every
// section, the shape of its content and sensible defaults. The builder, the
// review screen and the renderer all read from here, so adding a section type
// is a one-file change.
//
// Nothing brand-specific lives here. Colours, logo, typography and company
// details come from the tenant/environment configuration at render time.

export type InfographicSectionType =
  | "cover"
  | "client-info"
  | "executive-summary"
  | "understanding"
  | "proposed-solution"
  | "scope-of-work"
  | "services"
  | "deliverables"
  | "approach"
  | "timeline"
  | "pricing"
  | "addons"
  | "why-us"
  | "portfolio"
  | "testimonials"
  | "process"
  | "terms"
  | "next-steps"
  | "contact";

export interface InfographicSectionDef {
  id: InfographicSectionType;
  type: InfographicSectionType;
  title: string;
  /** What the BD sees when choosing which sections to include. */
  description: string;
  /** Lucide icon name used in the section picker / review navigation. */
  icon: string;
  /** Required sections cannot be removed — publishing validates them anyway. */
  required?: boolean;
  /** Sections that hold no BD-authored content are not offered as "empty". */
  defaultContent: any;
}

export const INFOGRAPHIC_SECTIONS: InfographicSectionDef[] = [
  {
    id: "cover",
    type: "cover",
    title: "Proposal Cover",
    description: "Title, client, date and headline credentials",
    icon: "LayoutTemplate",
    required: true,
    defaultContent: {
      eyebrow: "",
      headline: "",
      // The opening paragraph, then any further paragraphs.
      subheadline: "",
      ledes: [],
      // Credential rings, exactly as the reference hero shows them.
      badges: [
        { value: "100%", label: "Job Success", sub: "Upwork score" },
        { value: "★", label: "Top Rated", sub: "Upwork badge" },
        { value: "50+", label: "Projects", sub: "completed" },
      ],
    },
  },
  {
    id: "client-info",
    type: "client-info",
    title: "Client Information",
    description: "Who it is for — named in the footer, no card of its own",
    icon: "User",
    required: true,
    defaultContent: { name: "", company: "", email: "", phone: "", website: "", note: "" },
  },
  {
    id: "executive-summary",
    type: "executive-summary",
    title: "Executive Summary",
    description: "The short version for decision makers",
    icon: "FileText",
    defaultContent: {
      text: "",
      highlights: [
        { value: "", label: "Timeline" },
        { value: "", label: "Investment" },
        { value: "", label: "Deliverables" },
      ],
    },
  },
  {
    id: "understanding",
    type: "understanding",
    title: "Understanding of Requirements",
    description: "Prove you read the brief",
    icon: "Search",
    defaultContent: { text: "", points: [], callout: "" },
  },
  {
    id: "proposed-solution",
    type: "proposed-solution",
    title: "Proposed Solution",
    description: "The approach you are recommending",
    icon: "Lightbulb",
    defaultContent: {
      text: "",
      pillars: [
        { title: "", description: "", icon: "Target" },
        { title: "", description: "", icon: "Zap" },
        { title: "", description: "", icon: "Shield" },
      ],
    },
  },
  {
    id: "scope-of-work",
    type: "scope-of-work",
    title: "Scope of Work",
    description: "What is in — and what is not",
    icon: "ListChecks",
    defaultContent: { items: [], excluded: [] },
  },
  {
    id: "services",
    type: "services",
    title: "Services",
    description: "Service cards with what each one covers",
    icon: "LayoutGrid",
    defaultContent: { items: [] },
  },
  {
    id: "deliverables",
    type: "deliverables",
    title: "Deliverables",
    description: "Exactly what the client receives, and in which formats",
    icon: "Package",
    defaultContent: { items: [] },
  },
  {
    id: "approach",
    type: "approach",
    title: "Project Approach",
    description: "How the work will run",
    icon: "Route",
    defaultContent: { subtitle: "", steps: [] },
  },
  {
    id: "timeline",
    type: "timeline",
    title: "Milestones / Timeline",
    description: "Phases with durations and dates",
    icon: "CalendarClock",
    defaultContent: { milestones: [] },
  },
  {
    id: "pricing",
    type: "pricing",
    title: "Investment / Pricing",
    description: "Pricing options as comparable blocks",
    icon: "Receipt",
    // Three comparable options, as the reference lays them out. Prices start
    // empty on purpose: an option without one reads "Price on request" rather
    // than holding the whole proposal up while the number is settled.
    defaultContent: {
      currency: "USD",
      note: "",
      tiers: [
        { tag: "Essentials", name: "The lean option", price: "", unit: "fixed price", features: [], note: "", featured: false },
        { tag: "Signature", name: "The recommended fit", price: "", unit: "fixed price", features: [], note: "", featured: true },
        { tag: "Complete", name: "Everything, end to end", price: "", unit: "fixed price", features: [], note: "", featured: false },
      ],
    },
  },
  {
    id: "addons",
    type: "addons",
    title: "Optional Add-ons",
    description: "Extras the client can pick up later",
    icon: "PlusCircle",
    defaultContent: { items: [] },
  },
  {
    id: "why-us",
    type: "why-us",
    title: "Why Us",
    description: "Value proposition and proof",
    icon: "Award",
    defaultContent: { points: [], stats: [] },
  },
  {
    id: "portfolio",
    type: "portfolio",
    title: "Portfolio / Relevant Work",
    description: "Samples pulled from the Portfolio Library",
    icon: "Briefcase",
    // Shown as the hero's spotlight card. With both lists filled it renders the
    // reference's two tabs; with one, the single-label mode.
    defaultContent: {
      subtitle: "Recent work",
      primaryTabLabel: "",
      bestTabLabel: "",
      soloLabel: "",
      note: "",
      bestNote: "",
      projects: [],
      bestProjects: [],
    },
  },
  {
    id: "testimonials",
    type: "testimonials",
    title: "Client Reviews",
    description: "Star-rated quotes from past clients",
    icon: "Star",
    // Three slots ready for real quotes. The section stays out of the client's
    // copy until at least one is filled — invented reviews are worse than none.
    defaultContent: {
      items: [
        { quote: "", attribution: "", rating: "5" },
        { quote: "", attribution: "", rating: "5" },
        { quote: "", attribution: "", rating: "5" },
      ],
    },
  },
  {
    id: "process",
    type: "process",
    title: "Process / Workflow",
    description: "How you work day to day",
    icon: "Workflow",
    defaultContent: { steps: [] },
  },
  {
    id: "terms",
    type: "terms",
    title: "Terms & Conditions",
    description: "Payment, revisions, ownership",
    icon: "Scale",
    required: true,
    defaultContent: { items: [], text: "" },
  },
  {
    id: "next-steps",
    type: "next-steps",
    title: "Next Steps",
    description: "The call to action",
    icon: "ArrowRightCircle",
    required: true,
    // The closing card and the sticky bar are always part of the page, so this
    // one ships with working copy the BD can adjust rather than write.
    defaultContent: {
      text: "Reply here and I will send the discovery questionnaire the same day. No obligation — happy to answer questions first.",
      steps: [],
      buttonText: "Message me",
      buttonUrl: "",
    },
  },
  {
    id: "contact",
    type: "contact",
    title: "Contact Information",
    description: "Who signs the proposal and how to reach them — shown in the footer",
    icon: "Mail",
    required: true,
    defaultContent: { name: "", email: "", phone: "", website: "", note: "" },
  },
];

export const getInfographicSection = (type: string) =>
  INFOGRAPHIC_SECTIONS.find((s) => s.type === type);

// ── The proposal shape ───────────────────────────────────────────────────────
// One shape, matching the approved reference: hero, what I read in your brief,
// how it would run, investment, reviews, close. A longer proposal reads as a
// worse proposal, so nothing else ships by default — the BD adds any other
// section from the builder when a particular job actually needs it.
//
// "cover", "portfolio", "client-info" and "contact" carry data rather than
// blocks of their own: the first two are the hero, the last two name the
// parties in the footer.
export const REFERENCE_SECTIONS: InfographicSectionType[] = [
  "cover",
  "client-info",
  "portfolio",
  "understanding",
  "approach",
  "deliverables",
  "pricing",
  "testimonials",
  "next-steps",
  "contact",
];

// The heading each section carries on the client-facing page. The section
// picker and the review screen keep the longer, descriptive names.
const DEFAULT_HEADING: Partial<Record<InfographicSectionType, string>> = {
  understanding: "Here's what I'm reading in your brief.",
  "executive-summary": "The short version.",
  "proposed-solution": "What I'd propose.",
  "scope-of-work": "What's in scope.",
  approach: "Four steps, no guesswork in between.",
  process: "How the work runs day to day.",
  timeline: "How long each stage takes.",
  deliverables: "What you end up with.",
  services: "What's on the table.",
  pricing: "Ways to work together, same process underneath.",
  addons: "Worth adding later.",
  "why-us": "Why me for this one.",
  testimonials: "What it's actually like to work through this.",
  terms: "The terms, in plain words.",
  "next-steps": "If this reads right, let's make it real.",
};

// Build the starting sections. Content is empty by design — anything the
// system already knows is filled in by the builder from the client, the
// freelance profile and the Portfolio Library rather than typed twice.
export const buildInfographicSections = () => {
  return REFERENCE_SECTIONS.map((type, index) => {
    const def = getInfographicSection(type) as InfographicSectionDef;
    return {
      id: `${type}`,
      type,
      layout: "default",
      // The reference's own headings, which the BD can still rewrite.
      title: DEFAULT_HEADING[type] || def.title,
      subtitle: "",
      order: index + 1,
      hidden: false,
      content: JSON.parse(JSON.stringify(def.defaultContent)),
    };
  });
};

export default INFOGRAPHIC_SECTIONS;
